import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { ApiError } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { Icon } from '../shell.js';
import { Dialog, toast } from '../ui.js';
import { size } from './ProjectFiles.js';

/**
 * Rushes after a shoot (owner 2026-10-21): banners at the top of My work. The camera crew gets "Ladda upp rushes" with
 * the RUSHES folder in Drive and "Klart"; there is no close cross, it stays until it is done (or "Laddar upp i morgon",
 * "Någon annan laddar upp" under Fler val). The project's editors then get "Rushes är uppe" with "Uppfattat".
 *
 * After "Klart" the camera's banner becomes "Efter inspelningen" (owner 2026-10-30): the time (shoot + preparation +
 * travel, "Stämmer" or change it), "Hur gick inspelningen?" (quick picks, any number, and an optional text the editors
 * see in "Rushes är uppe") and 1–5 stars with a comment per actor booked that day. "Senare" asks again tomorrow at 08.
 */

interface Upload {
  shootId: string;
  project: { id: string; name: string };
  client: string | null;
  date: string;
  end: string;
  editors: string[];
  folders: { name: string; url: string | null }[];
  others: { id: string; name: string }[];
}
interface Ready {
  shootId: string;
  project: { id: string; name: string };
  client: string | null;
  date: string;
  by: string | null;
  folder: string | null;
  files: number | null;
  bytes: number | null;
  at: string;
  url: string | null;
  notes: ShootNote[];
  comments: string[];
}
interface Report {
  shootId: string;
  project: { id: string; name: string };
  client: string | null;
  date: string;
  start: string;
  end: string;
  /** the rushes are up; until then the rushes banner asks for them (30 minutes after the end) */
  rushesDone: boolean;
  folder: string | null;
  files: number | null;
  bytes: number | null;
  editors: string[];
  time: { onSite: number; prep: number; travel: number };
  actors: { castingId: string; name: string; role: string | null }[];
}
interface Mine {
  upload: Upload[];
  report: Report[];
  ready: Ready[];
}

/** "Hur gick inspelningen?" – the quick picks, in this order (the API keeps the same list). */
export const SHOOT_NOTES = ['as_planned', 'plan_changed', 'missing_shots', 'broll', 'sound', 'light_tech', 'late_short', 'cast_changed'] as const;
export type ShootNote = (typeof SHOOT_NOTES)[number];
export const NOTE_LABEL = texts<Record<ShootNote, string>>(
  {
    as_planned: 'Everything as planned',
    plan_changed: 'The client changed the plan on set',
    missing_shots: 'Missing shots for something in the plan',
    broll: 'Good extra footage (B-roll)',
    sound: 'Sound problems',
    light_tech: 'Light or technical problems',
    late_short: 'Late start or shorter shoot',
    cast_changed: 'Someone did not show up or was replaced'
  },
  {
    as_planned: 'Allt enligt plan',
    plan_changed: 'Kunden ändrade planen på plats',
    missing_shots: 'Saknar bilder till något i planen',
    broll: 'Bra extramaterial (B-roll) finns',
    sound: 'Ljudproblem',
    light_tech: 'Ljus- eller teknikproblem',
    late_short: 'Försenad start eller kortare inspelning',
    cast_changed: 'Medverkande uteblev eller byttes'
  }
);

type Done =
  | {
      done: true;
      files: number | null;
      bytes: number | null;
      editors: string[];
    }
  | { done: false; empty: true; folder: string; url: string | null };

const T = texts(
  {
    upload: (p: string) => `Upload the rushes from ${p}`,
    ended: (end: string, day: string) => `the shoot ended ${day}${end}`,
    waits: (names: string[]) => `${names.join(' and ')} ${names.length > 1 ? 'edit' : 'edits'} and is waiting for the footage`,
    folder: 'Folder:',
    open: (f: string) => `Open RUSHES/${f} in Drive`,
    done: 'Done, everything is uploaded',
    more: 'More options',
    later: 'Uploading tomorrow',
    laterDone: 'OK, we will ask again at 08:00 tomorrow.',
    other: 'Someone else uploads…',
    otherTitle: 'Who uploads the rushes?',
    otherSub: 'They get the reminder instead of you.',
    pick: 'Pick a person',
    handOver: 'Hand over',
    cancel: 'Cancel',
    handedOver: (n: string) => `${n} gets the reminder now.`,
    emptyTitle: (f: string) => `We see no files in RUSHES/${f} yet`,
    emptyBody: 'If Drive for desktop is still uploading you can press Done anyway. Otherwise open the folder and add the files.',
    doneAnyway: 'Done anyway',
    openFolder: 'Open the folder',
    thanks: (names: string[]) => (names.length ? `Thanks! ${names.join(' and ')} ${names.length > 1 ? 'have' : 'has'} been told the rushes are up.` : 'Thanks! The rushes are marked as up.'),
    count: (n: number, b: number | null, f: string, lang: 'en' | 'sv') => `${n} files${b ? ` · ${size(b, lang)}` : ''} in RUSHES/${f}`,
    ready: (p: string) => `The rushes are up for ${p}`,
    readyBy: (by: string | null, what: string) => `${by ?? 'Someone'} uploaded ${what}`,
    files: (n: number, b: number | null, lang: 'en' | 'sv') => `${n} files${b ? ` (${size(b, lang)})` : ''}`,
    inFolder: (f: string) => ` in RUSHES/${f}`,
    openDrive: 'Open in Drive',
    ack: 'Got it',
    today: 'today ',
    yesterday: 'yesterday ',
    after: (p: string) => `After the shoot: ${p}`,
    rushesStep: 'Rushes',
    rushesDone: (what: string, names: string[]) => `Done · ${what}.${names.length ? ` ${names.join(' and ')} ${names.length > 1 ? 'have' : 'has'} been told.` : ''}`,
    timeStep: 'Your time',
    timeSum: (start: string, end: string, onSite: string, prep: string, travel: string, sum: string) => [`Shoot ${start}–${end} `, onSite, ' + preparation ', prep, ' + travel ', travel, ' = ', sum],
    timeOk: 'Correct',
    timeEdit: 'Change',
    hours: 'Hours in total',
    noteStep: 'How did the shoot go?',
    notePlaceholder: 'Anything else the editor should know? (optional)',
    actorStep: 'Actors',
    rate: (n: string, s: number) => `${n}: ${s} of 5`,
    actorComment: 'Comment (optional)',
    actorHint: 'Only seen inside KFF. Keep it factual: the actor may ask to see what is written about them.',
    send: 'Send',
    reportLater: 'Later',
    sent: 'Thanks! The editor sees your comment with the rushes.',
    reportLaterDone: 'OK, we will ask again at 08:00 tomorrow.'
  },
  {
    upload: (p: string) => `Ladda upp rushes från ${p}`,
    ended: (end: string, day: string) => `inspelningen slutade ${day}${end}`,
    waits: (names: string[]) => `${names.join(' och ')} redigerar och väntar på rushes`,
    folder: 'Mapp:',
    open: (f: string) => `Öppna RUSHES/${f} i Drive`,
    done: 'Klart, allt är uppladdat',
    more: 'Fler val',
    later: 'Laddar upp i morgon',
    laterDone: 'Okej, vi påminner igen i morgon kl 08.',
    other: 'Någon annan laddar upp…',
    otherTitle: 'Vem laddar upp rushes?',
    otherSub: 'Den personen får påminnelsen i stället för dig.',
    pick: 'Välj person',
    handOver: 'Lämna över',
    cancel: 'Avbryt',
    handedOver: (n: string) => `${n} får påminnelsen nu.`,
    emptyTitle: (f: string) => `Vi ser inga filer i RUSHES/${f} än`,
    emptyBody: 'Om uppladdningen fortfarande pågår i Drive för datorn kan du trycka Klart ändå. Annars öppnar du mappen och lägger in filerna.',
    doneAnyway: 'Klart ändå',
    openFolder: 'Öppna mappen',
    thanks: (names: string[]) => (names.length ? `Tack! ${names.join(' och ')} har fått besked om att rushes är uppe.` : 'Tack! Rushes är markerade som uppladdade.'),
    count: (n: number, b: number | null, f: string, lang: 'en' | 'sv') => `${n} filer${b ? ` · ${size(b, lang)}` : ''} i RUSHES/${f}`,
    ready: (p: string) => `Rushes är uppe för ${p}`,
    readyBy: (by: string | null, what: string) => `${by ?? 'Någon'} laddade upp ${what}`,
    files: (n: number, b: number | null, lang: 'en' | 'sv') => `${n} filer${b ? ` (${size(b, lang)})` : ''}`,
    inFolder: (f: string) => ` i RUSHES/${f}`,
    openDrive: 'Öppna i Drive',
    ack: 'Uppfattat',
    today: '',
    yesterday: 'i går ',
    after: (p: string) => `Efter inspelningen: ${p}`,
    rushesStep: 'Rushes',
    rushesDone: (what: string, names: string[]) => `Klart · ${what}.${names.length ? ` ${names.join(' och ')} har fått besked.` : ''}`,
    timeStep: 'Din tid',
    timeSum: (start: string, end: string, onSite: string, prep: string, travel: string, sum: string) => [`Inspelning ${start}–${end} `, onSite, ' + förberedelse ', prep, ' + resa ', travel, ' = ', sum],
    timeOk: 'Stämmer',
    timeEdit: 'Ändra',
    hours: 'Timmar totalt',
    noteStep: 'Hur gick inspelningen?',
    notePlaceholder: 'Något mer klipparen ska veta? (valfritt)',
    actorStep: 'Skådespelare',
    rate: (n: string, s: number) => `${n}: ${s} av 5`,
    actorComment: 'Kommentar (valfritt)',
    actorHint: 'Syns bara internt på KFF. Skriv sakligt: skådespelaren kan begära ut det som står om hen.',
    send: 'Skicka',
    reportLater: 'Senare',
    sent: 'Tack! Klipparen ser din kommentar tillsammans med rushes.',
    reportLaterDone: 'Okej, vi frågar igen i morgon kl 08.'
  }
);

/** Hours as "7,5 h" / "7.5 h". */
const hrs = (min: number, lang: 'en' | 'sv') => `${(Math.round((min / 60) * 10) / 10).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-GB')} h`;

const stockholmDay = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(d);

/**
 * The banners, newest shoot first; nothing at all when there is nothing to do. `projectId`: only that project's (the
 * project's Shoots tab shows the same banner, so the camera can finish from there too, owner 2026-10-21).
 */
export function RushesBanners(p: { api: Api; projectId?: string }) {
  const [v, setV] = useState<Mine | null>(null);
  const [thanks, setThanks] = useState<{
    text: string;
    sub: string | null;
  } | null>(null);
  const load = () => p.api.get<Mine>('/api/rushes').then(setV, () => setV(null));
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (!thanks) return;
    const t = setTimeout(() => setThanks(null), 6000);
    return () => clearTimeout(t);
  }, [thanks]);
  if (!v && !thanks) return null;
  return (
    <div class="rush-stack">
      {/* "Efter inspelningen" comes first, from the planned end; the rushes 30 minutes later (owner 2026-10-30). Once the
          rushes are up, "Efter inspelningen" says so in its first row. */}
      {thanks && !v?.report?.length ? (
        <div class="rush rush--ok" role="status">
          <span class="rush-ico rush-ico--ok">
            <Icon name="check" />
          </span>
          <div class="rush-body">
            <strong>{thanks.text}</strong>
            {thanks.sub ? <p>{thanks.sub}</p> : null}
          </div>
        </div>
      ) : null}
      {(v?.report ?? []).filter((r) => !p.projectId || r.project.id === p.projectId).map((r) => (
        <ReportBanner key={r.shootId} api={p.api} r={r} reload={load} />
      ))}
      {v?.upload.filter((u) => !p.projectId || u.project.id === p.projectId).map((u) => (
        <UploadBanner key={u.shootId} api={p.api} u={u} reload={load} thanks={setThanks} />
      ))}
      {v?.ready.filter((r) => !p.projectId || r.project.id === p.projectId).map((r) => (
        <ReadyBanner key={r.shootId} api={p.api} r={r} reload={load} />
      ))}
    </div>
  );
}

function UploadBanner(p: { api: Api; u: Upload; reload: () => Promise<void>; thanks: (t: { text: string; sub: string | null }) => void }) {
  const lang = useLang();
  const t = T[lang];
  const { u } = p;
  const [folder, setFolder] = useState(u.folders[0]?.name ?? 'A-CAM');
  const [busy, setBusy] = useState(false);
  const [empty, setEmpty] = useState<{
    folder: string;
    url: string | null;
  } | null>(null);
  const [handing, setHanding] = useState(false);
  const url = u.folders.find((f) => f.name === folder)?.url ?? null;
  const today = stockholmDay(new Date());
  const yesterday = stockholmDay(new Date(Date.now() - 86_400_000));
  const day = u.date === today ? t.today : u.date === yesterday ? t.yesterday : `${Number(u.date.slice(8, 10))}/${Number(u.date.slice(5, 7))} `;
  const fail = (e: unknown) =>
    toast({
      text: e instanceof ApiError || e instanceof Error ? e.message : String(e)
    });
  const done = async (force: boolean) => {
    setBusy(true);
    try {
      const r = await p.api.post<Done>(`/api/rushes/${u.shootId}/done`, {
        folder,
        force
      });
      if (!r.done) {
        setEmpty({ folder: r.folder, url: r.url });
        return;
      }
      setEmpty(null);
      p.thanks({
        text: t.thanks(r.editors),
        sub: r.files ? t.count(r.files, r.bytes, folder, lang) : null
      });
      await p.reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const later = async () => {
    try {
      await p.api.post(`/api/rushes/${u.shootId}/later`);
      toast({ text: t.laterDone });
      await p.reload();
    } catch (e) {
      fail(e);
    }
  };
  return (
    <section class="rush" aria-label={t.upload(u.project.name)}>
      <span class="rush-ico role-ico--camera">
        <Icon name="video" />
      </span>
      <div class="rush-body">
        <strong>{t.upload(u.project.name)}</strong>
        <p>{[u.client, t.ended(u.end, day), u.editors.length ? t.waits(u.editors) : null].filter(Boolean).join(' · ')}</p>
        <div class="rush-folders" role="radiogroup" aria-label={t.folder}>
          <span class="small muted">{t.folder}</span>
          {u.folders.map((f) => (
            <button type="button" role="radio" aria-checked={folder === f.name} class={`rush-pill${folder === f.name ? ' is-on' : ''}`} onClick={() => setFolder(f.name)}>
              {f.name}
            </button>
          ))}
        </div>
        <div class="rush-actions">
          {url ? (
            <a class="btn btn-primary" href={url} target="_blank" rel="noopener">
              {t.open(folder)}
            </a>
          ) : null}
          <button type="button" class="btn" disabled={busy} onClick={() => void done(false)}>
            {t.done}
          </button>
        </div>
        <details class="rush-more">
          <summary>{t.more}</summary>
          <div class="rush-more-body">
            <button type="button" class="btn btn-sm btn-ghost" onClick={() => void later()}>
              {t.later}
            </button>
            {u.others.length ? (
              <button type="button" class="btn btn-sm btn-ghost" onClick={() => setHanding(true)}>
                {t.other}
              </button>
            ) : null}
          </div>
        </details>
      </div>
      {empty ? (
        <Dialog
          title={t.emptyTitle(empty.folder)}
          onClose={() => setEmpty(null)}
          foot={
            <>
              <button type="button" class="btn btn-ghost" onClick={() => setEmpty(null)}>
                {t.cancel}
              </button>
              {empty.url ? (
                <a class="btn" href={empty.url} target="_blank" rel="noopener" onClick={() => setEmpty(null)}>
                  {t.openFolder}
                </a>
              ) : null}
              <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void done(true)}>
                {t.doneAnyway}
              </button>
            </>
          }
        >
          <p>{t.emptyBody}</p>
        </Dialog>
      ) : null}
      {handing ? <HandOver api={p.api} u={u} close={() => setHanding(false)} reload={p.reload} /> : null}
    </section>
  );
}

function HandOver(p: { api: Api; u: Upload; close: () => void; reload: () => Promise<void> }) {
  const t = T[useLang()];
  const [who, setWho] = useState('');
  const send = async () => {
    if (!who) return;
    try {
      const r = await p.api.post<{ name: string }>(`/api/rushes/${p.u.shootId}/other`, { personId: who });
      toast({ text: t.handedOver(r.name) });
      p.close();
      await p.reload();
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  return (
    <Dialog
      title={t.otherTitle}
      sub={t.otherSub}
      onClose={p.close}
      onSubmit={() => void send()}
      foot={
        <>
          <button type="button" class="btn btn-ghost" onClick={p.close}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={!who}>
            {t.handOver}
          </button>
        </>
      }
    >
      <label class="field">
        <span class="label">{t.pick}</span>
        <select class="input input--wide" value={who} onChange={(e) => setWho((e.currentTarget as HTMLSelectElement).value)}>
          <option value="">–</option>
          {p.u.others.map((o) => (
            <option value={o.id}>{o.name}</option>
          ))}
        </select>
      </label>
    </Dialog>
  );
}

function ReadyBanner(p: { api: Api; r: Ready; reload: () => Promise<void> }) {
  const lang = useLang();
  const t = T[lang];
  const { r } = p;
  const at = new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', {
    timeZone: 'Europe/Stockholm',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(r.at));
  const what = `${r.files ? t.files(r.files, r.bytes, lang) : 'rushes'}${r.folder ? t.inFolder(r.folder) : ''}`;
  const ack = async () => {
    try {
      await p.api.post(`/api/rushes/${r.shootId}/ack`);
      await p.reload();
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  return (
    <section class="rush rush--ready" aria-label={t.ready(r.project.name)}>
      <span class="rush-ico role-ico--editor">
        <Icon name="scissors" />
      </span>
      <div class="rush-body">
        <strong>{t.ready(r.project.name)}</strong>
        <p>{[r.client, `${t.readyBy(r.by, what)} · ${at}`].filter(Boolean).join(' · ')}</p>
        <ShootNotes notes={r.notes} comments={r.comments} />
        <div class="rush-actions">
          {r.url ? (
            <a class="btn btn-primary" href={r.url} target="_blank" rel="noopener">
              {t.openDrive}
            </a>
          ) : null}
          <button type="button" class="btn" onClick={() => void ack()}>
            {t.ack}
          </button>
        </div>
      </div>
    </section>
  );
}

/** The camera's quick picks as labels and their texts, for the editors (also on the project's Shoots tab). */
export function ShootNotes(p: { notes: ShootNote[]; comments: string[] }) {
  const labels = NOTE_LABEL[useLang()];
  if (!p.notes.length && !p.comments.length) return null;
  return (
    <>
      {p.notes.length ? (
        <div class="rush-tags">
          {p.notes.map((n) => (
            <span key={n} class="rush-tag">
              {labels[n]}
            </span>
          ))}
        </div>
      ) : null}
      {p.comments.map((c, i) => (
        <p key={i} class="rush-quote">
          {c}
        </p>
      ))}
    </>
  );
}

/* ---------------- "Efter inspelningen" ---------------- */

function ReportBanner(p: { api: Api; r: Report; reload: () => Promise<void> }) {
  const lang = useLang();
  const t = T[lang];
  const labels = NOTE_LABEL[lang];
  const { r } = p;
  const suggested = r.time.onSite + r.time.prep + r.time.travel;
  // the steps are numbered after the rushes row when the rushes are already up
  const k = r.rushesDone ? 1 : 0;
  const [editTime, setEditTime] = useState(false);
  const [hours, setHours] = useState(String(Math.round((suggested / 60) * 10) / 10));
  const [notes, setNotes] = useState<ShootNote[]>([]);
  const [comment, setComment] = useState('');
  const [stars, setStars] = useState<Record<string, number>>({});
  const [said, setSaid] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const what = r.files ? t.count(r.files, r.bytes, r.folder ?? 'A-CAM', lang) : `RUSHES/${r.folder ?? 'A-CAM'}`;
  const toggle = (n: ShootNote) =>
    setNotes((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : n === 'as_planned' ? ['as_planned'] : [...cur.filter((x) => x !== 'as_planned'), n]));
  const minutes = () => {
    if (!editTime) return suggested;
    const h = Number(hours.replace(',', '.'));
    return Number.isFinite(h) && h >= 0 ? Math.min(24 * 60, Math.round(h * 60)) : suggested;
  };
  const send = async () => {
    setBusy(true);
    try {
      await p.api.post(`/api/rushes/${r.shootId}/report`, {
        minutes: minutes(),
        notes,
        comment,
        ratings: r.actors.filter((a) => stars[a.castingId]).map((a) => ({ castingId: a.castingId, stars: stars[a.castingId], comment: said[a.castingId] ?? '' }))
      });
      toast({ text: t.sent });
      await p.reload();
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  const later = async () => {
    try {
      await p.api.post(`/api/rushes/${r.shootId}/report/later`);
      toast({ text: t.reportLaterDone });
      await p.reload();
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    }
  };
  const sum = t.timeSum(r.start, r.end, hrs(r.time.onSite, lang), hrs(r.time.prep, lang), hrs(r.time.travel, lang), hrs(suggested, lang));
  return (
    <section class="rush" aria-label={t.after(r.project.name)}>
      <span class="rush-ico role-ico--camera">
        <Icon name="video" />
      </span>
      <div class="rush-body">
        <strong>{t.after(r.project.name)}</strong>
        <p>{[r.client, t.ended(r.end, `${Number(r.date.slice(8, 10))}/${Number(r.date.slice(5, 7))} `)].filter(Boolean).join(' · ')}</p>

        {r.rushesDone ? (
          <div class="rush-step rush-step--first">
            <p class="rush-step-h">
              <span class="rush-n rush-n--ok" aria-hidden="true">
                <Icon name="check" />
              </span>
              {t.rushesStep}
            </p>
            <p class="rush-step-sub">{t.rushesDone(what, r.editors)}</p>
          </div>
        ) : null}

        <div class={`rush-step${r.rushesDone ? '' : ' rush-step--first'}`}>
          <p class="rush-step-h">
            <span class="rush-n">{k + 1}</span>
            {t.timeStep}
          </p>
          <p class="rush-step-sub rush-sum">
            {sum.map((x, i) => (i % 2 === 1 ? <b key={i}>{x}</b> : x))}
          </p>
          <div class="rush-actions">
            <button type="button" class={`btn btn-sm${editTime ? ' btn-ghost' : ' rush-ok'}`} aria-pressed={!editTime} onClick={() => setEditTime(false)}>
              {!editTime ? <Icon name="check" /> : null}
              {t.timeOk}
            </button>
            <button type="button" class={`btn btn-sm${editTime ? '' : ' btn-ghost'}`} aria-pressed={editTime} onClick={() => setEditTime(true)}>
              {t.timeEdit}
            </button>
            {editTime ? (
              <label class="rush-hours">
                <span class="small muted">{t.hours}</span>
                <input class="input" inputMode="decimal" value={hours} onInput={(e) => setHours((e.currentTarget as HTMLInputElement).value)} />
              </label>
            ) : null}
          </div>
        </div>

        <div class="rush-step">
          <p class="rush-step-h">
            <span class="rush-n">{k + 2}</span>
            {t.noteStep}
          </p>
          <div class="rush-chips" role="group" aria-label={t.noteStep}>
            {SHOOT_NOTES.map((n) => (
              <button key={n} type="button" class={`rush-chip${notes.includes(n) ? ' is-on' : ''}`} aria-pressed={notes.includes(n)} onClick={() => toggle(n)}>
                {notes.includes(n) ? <Icon name="check" /> : null}
                {labels[n]}
              </button>
            ))}
          </div>
          <textarea class="input rush-text" rows={2} placeholder={t.notePlaceholder} aria-label={t.notePlaceholder} value={comment} onInput={(e) => setComment((e.currentTarget as HTMLTextAreaElement).value)} />
        </div>

        {r.actors.length ? (
          <div class="rush-step">
            <p class="rush-step-h">
              <span class="rush-n">{k + 3}</span>
              {t.actorStep}
            </p>
            {r.actors.map((a) => (
              <div key={a.castingId} class="rush-actor">
                <div>
                  <b>{a.name}</b>
                  {a.role ? <span class="small muted rush-actor-role">{a.role}</span> : null}
                </div>
                <div class="rush-stars" role="radiogroup" aria-label={a.name}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      role="radio"
                      aria-checked={stars[a.castingId] === n}
                      aria-label={t.rate(a.name, n)}
                      class={`rush-star${(stars[a.castingId] ?? 0) >= n ? ' is-on' : ''}`}
                      onClick={() => setStars({ ...stars, [a.castingId]: stars[a.castingId] === n ? 0 : n })}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <input class="input" placeholder={t.actorComment} aria-label={`${a.name}: ${t.actorComment}`} value={said[a.castingId] ?? ''} onInput={(e) => setSaid({ ...said, [a.castingId]: (e.currentTarget as HTMLInputElement).value })} />
              </div>
            ))}
            <p class="small muted rush-hint">{t.actorHint}</p>
          </div>
        ) : null}

        <div class="rush-actions rush-send">
          <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void send()}>
            {t.send}
          </button>
          <button type="button" class="btn btn-ghost" onClick={() => void later()}>
            {t.reportLater}
          </button>
        </div>
      </div>
    </section>
  );
}
